import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';
import { itemId, sendEvent } from '@theme/bh-gtag';

/* Keyed by request URL and cart: Shopify leaves whatever is in the cart out of
   its recommendations, so a product taken back out needs a fresh answer. */
/** @type {Map<string, string>} */
const recommendationsCache = new Map();

/* Lists already reported as seen on this page. The drawer and the cart page are
   re-rendered on every cart change, and a fresh element is not a fresh
   impression. */
/** @type {Set<string>} */
const seenLists = new Set();

/* All three change when the cart does. The seed product (the cart's first line
   in the cart surfaces) and the skip list decide what Shopify sends back, so
   either one changing means a fresh fetch. The remaining amount only re-picks
   which of the tiles held closes the gap. */
const REFETCH_ATTRIBUTES = ['data-product-id', 'data-skip-products'];
const WATCHED_ATTRIBUTES = [...REFETCH_ATTRIBUTES, 'data-remaining'];

class CartShippingTips extends Component {
  /** @type {HTMLElement[]} */
  #tileTemplates = [];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #didDebug = false;

  #attributeObserver = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => WATCHED_ATTRIBUTES.includes(mutation.attributeName ?? ''))) {
      // Hides a tile just added to the cart while the refetch below is in flight.
      this.#selectTiles();
    }
    if (mutations.some((mutation) => REFETCH_ATTRIBUTES.includes(mutation.attributeName ?? ''))) {
      this.#loadTips();
    }
  });

  connectedCallback() {
    super.connectedCallback();

    /* Quick-add clones the whole product page into its modal, this block
       included, and hides it there. A hidden copy has no business fetching
       recommendations or listening for cart updates. */
    if (this.closest('#quick-add-modal-content')) return;

    this.#attributeObserver.observe(this, {
      attributes: true,
      attributeFilter: WATCHED_ATTRIBUTES,
    });

    /* Only the product page asks for this. The cart drawer and cart page are
       re-rendered from Liquid on every cart change, and a second, client-side
       writer there would race the morph for the same numbers. */
    if (this.#bar?.dataset.liveUpdate === 'true') {
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    }

    if (!window.Shopify?.designMode) {
      this.#viewObserver.observe(this);
      // Capture, so a handler inside quick-add cannot stop it from being counted.
      this.addEventListener('click', this.#handleClick, { capture: true });
    }

    this.#loadTips();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#selectTiles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#attributeObserver.disconnect();
    this.#viewObserver.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /** The progress bar this element belongs to. @type {HTMLElement | null} */
  get #bar() {
    return this.closest('.cart-fs');
  }

  /* GA4 view_item_list once the tips are on screen, not merely rendered: the
     drawer's copy sits in a closed dialog until it opens, and on a phone the
     product page's is below the fold. Half the block, once per list per page.
     While no tile is shown the block is display: none and never intersects. */
  #viewObserver = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.intersectionRatio >= 0.5)) return;

      const list = this.dataset.bhList;
      if (!list || seenLists.has(list)) {
        this.#viewObserver.disconnect();
        return;
      }

      const tiles = this.#shownTiles();
      if (tiles.length === 0) return;

      seenLists.add(list);
      this.#viewObserver.disconnect();
      sendEvent('view_item_list', {
        item_list_name: list,
        items: tiles.map((tile, index) => this.#item(tile, index)),
      });
    },
    { threshold: 0.5 }
  );

  /* A tip taken either way is a select_item: the link opens the product, the +
     adds it straight away. GA4 carries the list name from here onto the
     add_to_cart and purchase that follow for the same item, which is how a
     report gets from "clicked in the drawer" to "sold from the drawer".

     ponytail: the item is the variant the tile shows. When + opens the variant
     picker and the shopper takes another size, the add_to_cart carries a
     different id and GA4 cannot pair the two; the click still counts. Upgrade
     path: report the picked variant from the quick-add modal. */
  #handleClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('a[href], .quick-add')) return;

    const tile = target.closest('.cart-fs-tip');
    const list = this.dataset.bhList;
    if (!(tile instanceof HTMLElement) || !list) return;

    sendEvent('select_item', {
      item_list_name: list,
      items: [this.#item(tile, this.#shownTiles().indexOf(tile))],
    });
  };

  /** The tiles on show, in the order the shopper sees them. @returns {HTMLElement[]} */
  #shownTiles() {
    const tiles = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.cart-fs-tip:not([hidden])')));
    // The closer is moved to the front by CSS `order`, not in the DOM.
    return tiles.sort(
      (a, b) => Number(b.classList.contains('cart-fs-tip--closer')) - Number(a.classList.contains('cart-fs-tip--closer'))
    );
  }

  /**
   * @param {HTMLElement} tile
   * @param {number} index - zero-based position among the tiles shown
   */
  #item(tile, index) {
    return {
      item_id: itemId(tile.dataset.productId ?? '', tile.dataset.variantId),
      item_name: tile.querySelector('.cart-fs-tip__title')?.textContent?.trim(),
      index: index + 1,
      item_list_name: this.dataset.bhList,
    };
  }

  /* Guards against a slow request overwriting a newer one — two quick taps on a
     quantity stepper are one event each, and they can land out of order. */
  #cartUpdateToken = 0;

  /* The bar is Liquid's, not this element's — but this module is the only script
     the block loads, and a second one for three lines of DOM would cost more
     than it explains. */
  #handleCartUpdate = async (event) => {
    const bar = this.#bar;
    if (!bar) return;

    const token = ++this.#cartUpdateToken;

    /* The theme dispatches this event when the change is REQUESTED, not when it
       has landed: component-cart-items and product-form both fire it and only
       then call the cart API. Reading the cart now would read the state we are
       trying to move away from — which is why adding, removing and stepping a
       quantity all left this bar showing the previous amount. The event carries
       the in-flight request as `promise`; the theme's own cart icon waits on the
       same one. A rejection means the change failed and the cart still says what
       the bar already says. */
    try {
      await event?.promise;
    } catch {
      return;
    }

    if (token !== this.#cartUpdateToken || !this.isConnected) return;

    const root = window.Shopify?.routes?.root ?? '/';

    let cart;
    try {
      const response = await fetch(`${root}cart.js`);
      if (!response.ok) throw new Error(`Cart request returned ${response.status}`);
      cart = await response.json();
    } catch {
      this.#debugFailure();
      return;
    }

    if (token !== this.#cartUpdateToken || !this.isConnected) return;

    const threshold = Number(bar.dataset.threshold);
    const total = Number(cart?.total_price);
    if (!Number.isFinite(threshold) || threshold <= 0 || !Number.isFinite(total)) return;

    const remaining = Math.max(threshold - total, 0);
    const percent = Math.min((total / threshold) * 100, 100);
    const done = remaining === 0;

    bar.style.setProperty('--cart-fs-pct', `${percent}%`);
    bar.classList.toggle('cart-fs--done', done);

    const track = bar.querySelector('[role="progressbar"]');
    track?.setAttribute('aria-valuenow', String(Math.round(percent)));

    const heading = bar.querySelector('[data-fs-heading]');
    if (heading instanceof HTMLElement) {
      heading.textContent = done ? bar.dataset.headingDone ?? '' : bar.dataset.headingProgress ?? '';
    }

    const amount = bar.querySelector('[data-fs-amount]');
    if (amount instanceof HTMLElement) {
      amount.textContent = done
        ? ''
        : formatMoney(remaining, bar.dataset.moneyFormat ?? '{{amount}}', window.Shopify?.currency?.active ?? '');
    }

    /* Writing these two is what re-runs the tile selection: the observer above
       is watching for exactly this. The seed product is the one being viewed,
       and it is never offered as a tip on its own page. */
    const cartProductIds = (cart?.items ?? []).map((/** @type {{ product_id: number }} */ item) => item.product_id);
    this.dataset.skipProducts = [...cartProductIds, this.dataset.productId].filter(Boolean).join(',');
    this.dataset.remaining = String(remaining);
  };

  async #loadTips() {
    const { productId, url } = this.dataset;
    if (!productId || !url) return;

    // A load for the previous cart must not land on top of this one.
    this.#activeFetch?.abort();

    let tiles = [];
    let failed = false;

    try {
      tiles = await this.#fetchTiles(url, productId, 'complementary');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      failed = true;
    }

    if (!this.isConnected) return;

    if (tiles.length === 0) {
      try {
        tiles = await this.#fetchTiles(url, productId, 'related');
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        failed = true;
      }
    }

    if (!this.isConnected) return;

    if (tiles.length === 0) {
      this.#clearTips();
      if (failed) this.#debugFailure();
      return;
    }

    this.#tileTemplates = tiles.map((tile) => document.importNode(tile, true));
    this.#renderTiles();
  }

  /**
   * @param {string} baseUrl
   * @param {string} productId
   * @param {'complementary' | 'related'} intent
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchTiles(baseUrl, productId, intent) {
    const requestUrl = `${baseUrl}?section_id=cart-shipping-tips&product_id=${productId}&limit=8&intent=${intent}`;
    const markup = await this.#fetchMarkup(requestUrl);
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const container = parsed.querySelector('#cart-shipping-tips');

    if (!container) return [];

    return Array.from(container.querySelectorAll('.cart-fs-tip')).filter((tile) => {
      if (!(tile instanceof HTMLElement)) return false;
      return Number.isFinite(Number(tile.dataset.price));
    });
  }

  /**
   * @param {string} requestUrl
   * @returns {Promise<string>}
   */
  async #fetchMarkup(requestUrl) {
    const cacheKey = `${requestUrl}|${this.dataset.skipProducts ?? ''}`;
    if (recommendationsCache.has(cacheKey)) {
      return recommendationsCache.get(cacheKey) ?? '';
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Recommendations request returned ${response.status}`);

      const markup = await response.text();
      recommendationsCache.set(cacheKey, markup);
      return markup;
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  #renderTiles() {
    const grid = this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;

    const tiles = this.#tileTemplates.map((tile) => /** @type {HTMLElement} */ (tile.cloneNode(true)));
    grid.replaceChildren(...tiles);
    this.#selectTiles();
  }

  #selectTiles() {
    const grid = this.querySelector('[ref="grid"]');
    const lead = this.querySelector('[ref="lead"]');
    if (!(grid instanceof HTMLElement) || !(lead instanceof HTMLElement)) return;

    let tiles = Array.from(grid.querySelectorAll('.cart-fs-tip')).filter(
      (tile) => tile instanceof HTMLElement && Number.isFinite(Number(tile.dataset.price))
    );

    if (tiles.length === 0 && this.#tileTemplates.length > 0) {
      tiles = this.#tileTemplates.map((tile) => /** @type {HTMLElement} */ (tile.cloneNode(true)));
      grid.replaceChildren(...tiles);
    }

    const remaining = Number(this.dataset.remaining);
    const max = Number(this.dataset.max);

    /* Products the block must not offer: everything already in the cart, plus
       the product being viewed when this runs on a product page. A tile whose
       product is in the cart is spent — adding it from here is what just
       happened, and offering it again reads as the block not noticing. The eight
       tiles fetched are the bench it draws replacements from. */
    const skip = new Set((this.dataset.skipProducts ?? '').split(',').filter(Boolean));
    const offerable = tiles.filter((tile) => !skip.has(tile.dataset.productId ?? ''));

    const sorted = [...offerable].sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));

    let closer;
    if (remaining > 0) {
      closer = sorted.find((tile) => Number(tile.dataset.price) >= remaining);
    }

    const rest = sorted.filter((tile) => tile !== closer);
    const visible = closer ? [closer, ...rest].slice(0, max) : sorted.slice(0, max);

    for (const tile of tiles) {
      tile.hidden = true;
      tile.classList.remove('cart-fs-tip--closer');
    }

    for (const tile of visible) tile.hidden = false;
    closer?.classList.add('cart-fs-tip--closer');

    lead.hidden = visible.length === 0;
  }

  #clearTips() {
    const grid = this.querySelector('[ref="grid"]');
    const lead = this.querySelector('[ref="lead"]');
    if (grid instanceof HTMLElement) grid.replaceChildren();
    if (lead instanceof HTMLElement) lead.hidden = true;
    this.#tileTemplates = [];
  }

  #debugFailure() {
    if (this.#didDebug) return;
    this.#didDebug = true;
    console.debug('Cart shipping tips could not be loaded.');
  }
}

if (!customElements.get('cart-shipping-tips')) {
  customElements.define('cart-shipping-tips', CartShippingTips);
}
